import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Autocomplete,
    Chip,
    Paper,
    Divider,
    MenuItem,
    InputAdornment,
    Tooltip,
    Collapse,
    Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BalanceIcon from '@mui/icons-material/Balance';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { getInvestidores, addInvestidor } from '../services/api';
import { Investidor, ObraResponsavel, ObraUnidadeInput } from '../services/types';
import { formatCurrency } from '../utils/formatters';

interface ObraResponsaveisUnidadesSectionProps {
    open: boolean;
    mode?: 'all' | 'responsaveis' | 'unidades';
    tipoConstrucao?: string;
    orcamentoInicial: number;
    quantidadeUnidadesManual: number | string;
    valorVendaUnidadeManual: number | string;
    onManualFieldsChange: (qtd: number | string, valorMedio: number | string) => void;
    responsaveis: ObraResponsavel[];
    onResponsaveisChange: (responsaveis: ObraResponsavel[]) => void;
    unidades: ObraUnidadeInput[];
    onUnidadesChange: (unidades: ObraUnidadeInput[]) => void;
}

export const ObraResponsaveisUnidadesSection: React.FC<ObraResponsaveisUnidadesSectionProps> = ({
    open,
    mode = 'all',
    tipoConstrucao,
    orcamentoInicial,
    quantidadeUnidadesManual,
    valorVendaUnidadeManual,
    onManualFieldsChange,
    responsaveis,
    onResponsaveisChange,
    unidades,
    onUnidadesChange,
}) => {
    const [investidoresCatalog, setInvestidoresCatalog] = useState<Investidor[]>([]);
    const [loadingInvestidores, setLoadingInvestidores] = useState(false);

    // Mini-form para criar investidor rápido sem sair do modal da obra
    const [showNewInvestidor, setShowNewInvestidor] = useState(false);
    const [novoInvNome, setNovoInvNome] = useState('');
    const [novoInvDoc, setNovoInvDoc] = useState('');
    const [novoInvContato, setNovoInvContato] = useState('');
    const [savingInv, setSavingInv] = useState(false);

    // Gerador rápido em lote
    const [showBatchGenerator, setShowBatchGenerator] = useState(false);
    const [batchCount, setBatchCount] = useState<number | string>(4);
    const [batchBaseValue, setBatchBaseValue] = useState<number | string>('');
    const [batchAreaConstruida, setBatchAreaConstruida] = useState<number | string>('');
    const [batchAreaNaoConstruida, setBatchAreaNaoConstruida] = useState<number | string>('');
    const [batchVagas, setBatchVagas] = useState<number | string>('');

    useEffect(() => {
        if (open && (mode === 'all' || mode === 'responsaveis')) {
            setLoadingInvestidores(true);
            getInvestidores()
                .then((data) => setInvestidoresCatalog(data || []))
                .catch((err) => console.error('Erro ao carregar investidores:', err))
                .finally(() => setLoadingInvestidores(false));
        }
    }, [open, mode]);

    const handleQuickCreateInvestidor = async () => {
        if (!novoInvNome.trim()) return;
        setSavingInv(true);
        try {
            const created = await addInvestidor({
                nome: novoInvNome.trim(),
                cpf_cnpj: novoInvDoc.trim(),
                contato: novoInvContato.trim(),
            });
            if (created && created.length > 0) {
                const novo = created[0] as Investidor;
                setInvestidoresCatalog((prev) => [...prev, novo].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
                onResponsaveisChange([
                    ...responsaveis,
                    {
                        id: novo.id,
                        nome: novo.nome,
                        cpf_cnpj: novo.cpf_cnpj,
                        contato: novo.contato,
                        percentual_participacao: null,
                    },
                ]);
                setNovoInvNome('');
                setNovoInvDoc('');
                setNovoInvContato('');
                setShowNewInvestidor(false);
            }
        } catch (err) {
            console.error('Erro ao criar investidor rápido:', err);
        } finally {
            setSavingInv(false);
        }
    };

    const handleDivideEqualParticipation = () => {
        if (responsaveis.length === 0) return;
        const share = Number((100 / responsaveis.length).toFixed(2));
        const updated = responsaveis.map((r, idx) => ({
            ...r,
            percentual_participacao:
                idx === responsaveis.length - 1
                    ? Number((100 - share * (responsaveis.length - 1)).toFixed(2))
                    : share,
        }));
        onResponsaveisChange(updated);
    };

    const handleUpdatePercentual = (id: number, value: string) => {
        const num = value === '' ? null : Math.min(100, Math.max(0, Number(value)));
        onResponsaveisChange(
            responsaveis.map((r) => (r.id === id ? { ...r, percentual_participacao: num } : r))
        );
    };

    const syncTotalsToObraFields = (nextUnidades: ObraUnidadeInput[]) => {
        onUnidadesChange(nextUnidades);
        if (nextUnidades.length > 0) {
            const somaValores = nextUnidades.reduce((acc, u) => acc + (Number(u.valor) || 0), 0);
            const media = nextUnidades.length > 0 ? Number((somaValores / nextUnidades.length).toFixed(2)) : 0;
            onManualFieldsChange(nextUnidades.length, media);
        }
    };

    const prefixUnidade = tipoConstrucao === 'casa' ? 'Casa' : 'Unidade';

    const handleAddUnidade = () => {
        const nextNum = String(unidades.length + 1).padStart(2, '0');
        const defaultVal = Number(valorVendaUnidadeManual) || 0;
        const nextList: ObraUnidadeInput[] = [
            ...unidades,
            {
                identificacao: `${prefixUnidade} ${nextNum}`,
                valor: defaultVal,
                area: null,
                area_construida: null,
                area_nao_construida: null,
                area_privativa_total: null,
                vagas: null,
                status: 'Disponível',
            },
        ];
        syncTotalsToObraFields(nextList);
    };

    const handleDuplicateUnidade = (idx: number) => {
        const source = unidades[idx];
        const nextNum = String(unidades.length + 1).padStart(2, '0');
        const nextList: ObraUnidadeInput[] = [
            ...unidades,
            {
                identificacao: `${prefixUnidade} ${nextNum}`,
                valor: Number(source.valor) || 0,
                area: source.area_privativa_total ?? source.area ?? null,
                area_construida: source.area_construida ?? null,
                area_nao_construida: source.area_nao_construida ?? null,
                area_privativa_total: source.area_privativa_total ?? source.area ?? null,
                vagas: source.vagas ?? null,
                status: 'Disponível',
                descricao: source.descricao,
            },
        ];
        syncTotalsToObraFields(nextList);
    };

    const handleRemoveUnidade = (idx: number) => {
        const nextList = unidades.filter((_, i) => i !== idx);
        syncTotalsToObraFields(nextList);
    };

    const handleUnidadeChange = (idx: number, field: keyof ObraUnidadeInput, value: any) => {
        const nextList = unidades.map((u, i) => {
            if (i !== idx) return u;
            const updated: ObraUnidadeInput = { ...u, [field]: value };

            if (field === 'area_construida' || field === 'area_nao_construida') {
                const constr = updated.area_construida != null ? Number(updated.area_construida) : null;
                const naoConstr = updated.area_nao_construida != null ? Number(updated.area_nao_construida) : null;
                if (constr != null || naoConstr != null) {
                    const totalPriv = Number(((constr || 0) + (naoConstr || 0)).toFixed(2));
                    updated.area_privativa_total = totalPriv;
                    updated.area = totalPriv;
                } else {
                    updated.area_privativa_total = null;
                    updated.area = null;
                }
            } else if (field === 'area_privativa_total' || field === 'area') {
                updated.area_privativa_total = value;
                updated.area = value;
            }

            return updated;
        });
        syncTotalsToObraFields(nextList);
    };

    const handleGenerateBatch = () => {
        const count = Math.min(200, Math.max(1, Math.trunc(Number(batchCount) || 0)));
        if (count <= 0) return;

        const baseVal = Number(batchBaseValue) || Number(valorVendaUnidadeManual) || 0;
        const baseConstr =
            batchAreaConstruida !== '' && Number(batchAreaConstruida) >= 0 ? Number(batchAreaConstruida) : null;
        const baseNaoConstr =
            batchAreaNaoConstruida !== '' && Number(batchAreaNaoConstruida) >= 0
                ? Number(batchAreaNaoConstruida)
                : null;
        const basePrivativa =
            baseConstr != null || baseNaoConstr != null
                ? Number(((baseConstr || 0) + (baseNaoConstr || 0)).toFixed(2))
                : null;
        const baseVagas = batchVagas !== '' && Number(batchVagas) >= 0 ? Math.trunc(Number(batchVagas)) : null;
        const startIdx = unidades.length + 1;

        const generated: ObraUnidadeInput[] = Array.from({ length: count }, (_, i) => {
            const num = String(startIdx + i).padStart(2, '0');
            return {
                identificacao: `${prefixUnidade} ${num}`,
                valor: baseVal,
                area: basePrivativa,
                area_construida: baseConstr,
                area_nao_construida: baseNaoConstr,
                area_privativa_total: basePrivativa,
                vagas: baseVagas,
                status: 'Disponível',
            };
        });

        syncTotalsToObraFields([...unidades, ...generated]);
        setShowBatchGenerator(false);
    };

    const totalPercentualResponsaveis = responsaveis.reduce(
        (acc, r) => acc + (Number(r.percentual_participacao) || 0),
        0
    );

    const vgvUnidades = unidades.reduce((acc, u) => acc + (Number(u.valor) || 0), 0);
    const vgvManual = (Number(quantidadeUnidadesManual) || 0) * (Number(valorVendaUnidadeManual) || 0);
    const vgvFinal = unidades.length > 0 ? vgvUnidades : vgvManual;
    const qtdFinal = unidades.length > 0 ? unidades.length : Number(quantidadeUnidadesManual) || 0;
    const ticketMedio = qtdFinal > 0 ? vgvFinal / qtdFinal : 0;
    const lucroProjetado = vgvFinal - (Number(orcamentoInicial) || 0);

    const areaConstruidaTotal = unidades.reduce((acc, u) => acc + (Number(u.area_construida) || 0), 0);
    const areaNaoConstruidaTotal = unidades.reduce((acc, u) => acc + (Number(u.area_nao_construida) || 0), 0);
    const areaPrivativaTotalObra = unidades.reduce(
        (acc, u) =>
            acc +
            (Number(
                u.area_privativa_total ??
                    u.area ??
                    ((Number(u.area_construida) || 0) + (Number(u.area_nao_construida) || 0) || 0)
            ) || 0),
        0
    );
    const totalVagasObra = unidades.reduce((acc, u) => acc + (Number(u.vagas) || 0), 0);

    const showResponsaveis = mode === 'all' || mode === 'responsaveis';
    const showUnidades = mode === 'all' || mode === 'unidades';

    return (
        <>
            {/* 1. SEÇÃO DE RESPONSÁVEIS (INVESTIDORES) */}
            {showResponsaveis && (
                <>
                    {mode === 'all' && <Divider>Responsável(is) pela Obra (Investidores)</Divider>}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Investidores Responsáveis pela Obra
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Selecione um ou mais investidores responsáveis pelo empreendimento e, se desejar, defina o percentual de participação de cada um.
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <Autocomplete
                                multiple
                                loading={loadingInvestidores}
                                options={investidoresCatalog}
                                getOptionLabel={(opt) => (opt.cpf_cnpj ? `${opt.nome} (${opt.cpf_cnpj})` : opt.nome)}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                value={investidoresCatalog.filter((inv) => responsaveis.some((r) => r.id === inv.id))}
                                onChange={(_e, selectedList) => {
                                    const updated: ObraResponsavel[] = selectedList.map((inv) => {
                                        const existing = responsaveis.find((r) => r.id === inv.id);
                                        return {
                                            id: inv.id,
                                            nome: inv.nome,
                                            cpf_cnpj: inv.cpf_cnpj,
                                            contato: inv.contato,
                                            percentual_participacao: existing?.percentual_participacao ?? null,
                                        };
                                    });
                                    onResponsaveisChange(updated);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Selecione um ou mais Investidores Responsáveis"
                                        placeholder="Buscar investidor..."
                                        size="small"
                                    />
                                )}
                                sx={{ flexGrow: 1, minWidth: 280 }}
                            />

                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PersonAddAlt1Icon />}
                                onClick={() => setShowNewInvestidor((prev) => !prev)}
                                sx={{ height: 40, whiteSpace: 'nowrap' }}
                            >
                                Novo Investidor
                            </Button>
                        </Box>

                        {/* Mini-formulário para cadastrar novo investidor sem sair da tela */}
                        <Collapse in={showNewInvestidor}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                                    Cadastrar Novo Investidor Rapidamente
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <TextField
                                        size="small"
                                        label="Nome do Investidor *"
                                        value={novoInvNome}
                                        onChange={(e) => setNovoInvNome(e.target.value)}
                                        sx={{ flexGrow: 1, minWidth: 200, bgcolor: 'background.paper' }}
                                    />
                                    <TextField
                                        size="small"
                                        label="CPF / CNPJ"
                                        value={novoInvDoc}
                                        onChange={(e) => setNovoInvDoc(e.target.value)}
                                        sx={{ width: 170, bgcolor: 'background.paper' }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Contato / Telefone"
                                        value={novoInvContato}
                                        onChange={(e) => setNovoInvContato(e.target.value)}
                                        sx={{ width: 160, bgcolor: 'background.paper' }}
                                    />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleQuickCreateInvestidor}
                                        disabled={savingInv || !novoInvNome.trim()}
                                    >
                                        {savingInv ? 'Salvando...' : 'Adicionar'}
                                    </Button>
                                </Box>
                            </Paper>
                        </Collapse>

                        {/* Lista de responsáveis selecionados com % de participação opcional */}
                        {responsaveis.length > 0 ? (
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 1,
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        Investidores Responsáveis Selecionados ({responsaveis.length}) — % de Participação (Opcional):
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<BalanceIcon fontSize="small" />}
                                        onClick={handleDivideEqualParticipation}
                                        sx={{ fontSize: '0.75rem', py: 0 }}
                                    >
                                        Dividir Igualitariamente (100%)
                                    </Button>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {responsaveis.map((resp) => (
                                        <Box
                                            key={resp.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                px: 1.5,
                                                py: 0.75,
                                                borderRadius: 1,
                                                bgcolor: 'action.hover',
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                    {resp.nome}
                                                </Typography>
                                                {resp.cpf_cnpj && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Doc: {resp.cpf_cnpj}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <TextField
                                                size="small"
                                                label="Participação"
                                                type="number"
                                                value={resp.percentual_participacao ?? ''}
                                                onChange={(e) => handleUpdatePercentual(resp.id, e.target.value)}
                                                inputProps={{ min: 0, max: 100, step: '0.01' }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                }}
                                                sx={{ width: 135, bgcolor: 'background.paper' }}
                                            />
                                        </Box>
                                    ))}
                                </Box>

                                {totalPercentualResponsaveis > 0 && Math.abs(totalPercentualResponsaveis - 100) > 0.1 && (
                                    <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                                        A soma das participações informadas é de{' '}
                                        <strong>{totalPercentualResponsaveis.toFixed(2).replace('.', ',')}%</strong>.
                                    </Alert>
                                )}
                            </Paper>
                        ) : (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    borderStyle: 'dashed',
                                    borderRadius: 1.5,
                                    color: 'text.secondary',
                                }}
                            >
                                <Typography variant="body2">
                                    Nenhum investidor responsável selecionado para esta obra.
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                </>
            )}

            {/* 2. SEÇÃO DE UNIDADES E VALORES DE VENDA SEPARADOS */}
            {showUnidades && (
                <>
                    {mode === 'all' && <Divider>Unidades da Obra, Áreas e Valores de Venda</Divider>}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Unidades Individualizadas ({unidades.length})
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    A Área Privativa Total é calculada automaticamente ao preencher a Área Construída ou Não Construída.
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AutoFixHighIcon />}
                                    onClick={() => setShowBatchGenerator((prev) => !prev)}
                                >
                                    Gerar em Lote
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddUnidade}
                                >
                                    Adicionar Unidade
                                </Button>
                            </Box>
                        </Box>

                        {/* Gerador Rápido em Lote */}
                        <Collapse in={showBatchGenerator}>
                            <Paper variant="outlined" sx={{ p: 1.75, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 700 }}>
                                    Gerar Unidades Numeradas Automaticamente
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <TextField
                                        size="small"
                                        label="Qtd. Unidades"
                                        type="number"
                                        value={batchCount}
                                        onChange={(e) => setBatchCount(e.target.value)}
                                        inputProps={{ min: 1, max: 200 }}
                                        sx={{ width: 125, bgcolor: 'background.paper' }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Valor Base (R$)"
                                        type="number"
                                        value={batchBaseValue}
                                        onChange={(e) => setBatchBaseValue(e.target.value)}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                        }}
                                        sx={{ flexGrow: 1, minWidth: 165, bgcolor: 'background.paper' }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Área Construída (m²)"
                                        type="number"
                                        value={batchAreaConstruida}
                                        onChange={(e) => setBatchAreaConstruida(e.target.value)}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        sx={{ width: 160, bgcolor: 'background.paper' }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Área Não Constr. (m²)"
                                        type="number"
                                        value={batchAreaNaoConstruida}
                                        onChange={(e) => setBatchAreaNaoConstruida(e.target.value)}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        sx={{ width: 170, bgcolor: 'background.paper' }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Vagas"
                                        type="number"
                                        value={batchVagas}
                                        onChange={(e) => setBatchVagas(e.target.value)}
                                        inputProps={{ min: 0, step: 1 }}
                                        sx={{ width: 95, bgcolor: 'background.paper' }}
                                    />
                                    <Button variant="contained" size="small" onClick={handleGenerateBatch} sx={{ height: 40 }}>
                                        Gerar {Number(batchCount) || 0} Unidades
                                    </Button>
                                </Box>
                            </Paper>
                        </Collapse>

                        {/* Lista de Unidades em Linha Única Compacta */}
                        {unidades.length > 0 ? (
                            <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                                {/* Cabeçalho de colunas visível em telas médias/grandes */}
                                <Box
                                    sx={{
                                        display: { xs: 'none', md: 'grid' },
                                        gridTemplateColumns: '1.3fr 1.35fr 1fr 1fr 1fr 0.6fr 0.95fr 68px',
                                        gap: 1,
                                        px: 1.5,
                                        py: 1,
                                        bgcolor: 'action.hover',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                        Identificação
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                        Valor de Venda (R$)
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                        Área Construída
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                        Área Não Constr.
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                        Área Priv. Total
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                        Vagas
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                        Status
                                    </Typography>
                                    <Typography variant="caption" align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                        Ações
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        maxHeight: 380,
                                        overflowY: 'auto',
                                        divideY: '1px solid',
                                    }}
                                >
                                    {unidades.map((unid, index) => (
                                        <Box
                                            key={unid.id ? `id_${unid.id}` : `idx_${index}`}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                    xs: '1fr 1fr',
                                                    md: '1.3fr 1.35fr 1fr 1fr 1fr 0.6fr 0.95fr 68px',
                                                },
                                                gap: 1,
                                                alignItems: 'center',
                                                px: 1.5,
                                                py: 1,
                                                borderBottom: index < unidades.length - 1 ? '1px solid' : 'none',
                                                borderColor: 'divider',
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            <TextField
                                                size="small"
                                                placeholder="Ex: Casa 01"
                                                value={unid.identificacao}
                                                onChange={(e) => handleUnidadeChange(index, 'identificacao', e.target.value)}
                                                required
                                                sx={{ bgcolor: 'background.paper' }}
                                            />

                                            <TextField
                                                size="small"
                                                type="number"
                                                placeholder="0,00"
                                                value={unid.valor === 0 ? '' : unid.valor}
                                                onChange={(e) =>
                                                    handleUnidadeChange(
                                                        index,
                                                        'valor',
                                                        e.target.value === '' ? 0 : Number(e.target.value)
                                                    )
                                                }
                                                inputProps={{ min: 0, step: '0.01' }}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                                }}
                                                sx={{ bgcolor: 'background.paper' }}
                                            />

                                            <TextField
                                                size="small"
                                                type="number"
                                                placeholder="Construída"
                                                value={unid.area_construida ?? ''}
                                                onChange={(e) =>
                                                    handleUnidadeChange(
                                                        index,
                                                        'area_construida',
                                                        e.target.value === '' ? null : Number(e.target.value)
                                                    )
                                                }
                                                inputProps={{ min: 0, step: '0.01' }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                                                }}
                                                sx={{ bgcolor: 'background.paper' }}
                                            />

                                            <TextField
                                                size="small"
                                                type="number"
                                                placeholder="Não Constr."
                                                value={unid.area_nao_construida ?? ''}
                                                onChange={(e) =>
                                                    handleUnidadeChange(
                                                        index,
                                                        'area_nao_construida',
                                                        e.target.value === '' ? null : Number(e.target.value)
                                                    )
                                                }
                                                inputProps={{ min: 0, step: '0.01' }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                                                }}
                                                sx={{ bgcolor: 'background.paper' }}
                                            />

                                            <TextField
                                                size="small"
                                                type="number"
                                                placeholder="Priv. Total"
                                                value={unid.area_privativa_total ?? unid.area ?? ''}
                                                onChange={(e) =>
                                                    handleUnidadeChange(
                                                        index,
                                                        'area_privativa_total',
                                                        e.target.value === '' ? null : Number(e.target.value)
                                                    )
                                                }
                                                inputProps={{ min: 0, step: '0.01' }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                                                }}
                                                sx={{ bgcolor: 'background.paper' }}
                                            />

                                            <TextField
                                                size="small"
                                                type="number"
                                                placeholder="0"
                                                value={unid.vagas ?? ''}
                                                onChange={(e) =>
                                                    handleUnidadeChange(
                                                        index,
                                                        'vagas',
                                                        e.target.value === '' ? null : Math.trunc(Number(e.target.value))
                                                    )
                                                }
                                                inputProps={{ min: 0, step: 1 }}
                                                sx={{ bgcolor: 'background.paper' }}
                                            />

                                            <TextField
                                                select
                                                size="small"
                                                value={unid.status || 'Disponível'}
                                                onChange={(e) => handleUnidadeChange(index, 'status', e.target.value)}
                                                sx={{ bgcolor: 'background.paper' }}
                                            >
                                                <MenuItem value="Disponível">Disponível</MenuItem>
                                                <MenuItem value="Reservada">Reservada</MenuItem>
                                                <MenuItem value="Vendida">Vendida</MenuItem>
                                            </TextField>

                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                <Tooltip title="Duplicar esta unidade">
                                                    <IconButton size="small" onClick={() => handleDuplicateUnidade(index)}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Remover unidade">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveUnidade(index)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        ) : (
                            /* Campos de estimativa rápida caso o usuário ainda não queira detalhar cada unidade */
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                    Nenhuma unidade individual cadastrada. Você pode informar abaixo uma estimativa geral ou clicar em{' '}
                                    <strong>"Adicionar Unidade"</strong> / <strong>"Gerar em Lote"</strong> acima para separar os valores e metragens de cada unidade:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <TextField
                                        size="small"
                                        name="quantidade_unidades"
                                        label="Quantidade Total de Unidades"
                                        type="number"
                                        value={quantidadeUnidadesManual}
                                        onChange={(e) => onManualFieldsChange(e.target.value, valorVendaUnidadeManual)}
                                        inputProps={{ min: 0, step: 1 }}
                                        sx={{ flexGrow: 1, minWidth: 200, bgcolor: 'background.paper' }}
                                    />
                                    <TextField
                                        size="small"
                                        name="valor_venda_unidade"
                                        label="Valor Médio de Venda por Unidade"
                                        type="number"
                                        value={valorVendaUnidadeManual ?? ''}
                                        onChange={(e) => onManualFieldsChange(quantidadeUnidadesManual, e.target.value)}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        sx={{ flexGrow: 1, minWidth: 220, bgcolor: 'background.paper' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                        }}
                                    />
                                </Box>
                            </Paper>
                        )}

                        {/* Resumo Financeiro Consolidado de Unidades e VGV */}
                        {(vgvFinal > 0 || unidades.length > 0) && (
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: 1.5,
                                    bgcolor: 'action.hover',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Chip
                                        label={`${qtdFinal} ${qtdFinal === 1 ? 'Unidade' : 'Unidades'}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontWeight: 700 }}
                                    />
                                    {areaConstruidaTotal > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Construída: <strong>{areaConstruidaTotal.toLocaleString('pt-BR')} m²</strong>
                                        </Typography>
                                    )}
                                    {areaNaoConstruidaTotal > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Não Construída: <strong>{areaNaoConstruidaTotal.toLocaleString('pt-BR')} m²</strong>
                                        </Typography>
                                    )}
                                    {areaPrivativaTotalObra > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Privativa Total: <strong>{areaPrivativaTotalObra.toLocaleString('pt-BR')} m²</strong>
                                        </Typography>
                                    )}
                                    {totalVagasObra > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Vagas: <strong>{totalVagasObra}</strong>
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        Ticket Médio: <strong>{formatCurrency(ticketMedio)}</strong>
                                    </Typography>
                                    {Number(orcamentoInicial) > 0 && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: lucroProjetado >= 0 ? 'success.main' : 'error.main',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Resultado Previsto: {formatCurrency(lucroProjetado)}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        VGV Total (Soma das Unidades)
                                    </Typography>
                                    <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 800 }}>
                                        {formatCurrency(vgvFinal)}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </>
    );
};
